import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import round from "lodash/round";

import NavigationComponent from "../components/NavigationComponent";
import RangeContainerComponent from "../readiness/RangeContainerComponent";
import { RPEList } from "../session-rpe/RPEList";
import { DATE_FORMATS } from "../../../../constants";
import {
  getLabels,
  readyChartData,
  mapUtcToDatePicker,
  filterUnsetValues,
  sortStatsByDate,
  filterStatsbyDateRange,
} from "../../../../utils/daily-stats";
import { AthleteLinks } from "../../../../parts/athlete-links";
import BarChartComponent from "../step-count/BarGraph";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { ReportsBackHandler } from "../components/ReportsBackHandler";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";
import { useRillContext } from "../../../../rill/RillReact/share/base";

const FIELD = "waterIntake";
const OZ_PER_L = 33.8140227;

function WaterIntake() {
  const { t } = useTranslation("athlete-portal");
  const { locals } = useRillContext();
  const { query, pathname, activeUser } = locals;
  const isMetric = activeUser?.units === "metric";

  const [currentAthlete, setCurrentAthlete] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [rangeSelector, setRangeSelector] = useState({
    type: "months",
    value: 6,
  });
  const [statsData, setStatsData] = useState([]);
  const [hoveredDate, setHoveredDate] = useState("");
  const [sortedRecent, setSortedRecent] = useState(true);
  const [sortedTableData, setSortedTableData] = useState(null);

  const dailyStats = locals.dailyStats || [];

  const getDateFirstEntry = useCallback(() => {
    const firstEntry = [...dailyStats]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .find((stats) => stats[FIELD] != null);
    return firstEntry && firstEntry.date;
  }, [dailyStats]);

  useEffect(() => {
    const { value, type } = rangeSelector;
    const stats = sortStatsByDate(
      filterUnsetValues(dailyStats, FIELD),
      "recent"
    );
    setStatsData(stats);
    if (rangeSelector.type !== "lifetime") {
      setDateFrom(
        moment(new Date(), DATE_FORMATS.DATE_DAY_FORMAT)
          .subtract(value, type)
          .format(DATE_FORMATS.DATE_PICKER_FORMAT)
      );
    }
  }, [dailyStats, rangeSelector.type, rangeSelector.value]);

  const athleteId = locals.athlete?._id ?? locals.activeUser?._id;
  useEffect(() => {
    if (athleteId !== currentAthlete) {
      setCurrentAthlete(athleteId);
      if (rangeSelector.type === "lifetime") {
        setDateFrom(
          moment("2015-12-31T07:57:45.121Z").format(
            DATE_FORMATS.DATE_PICKER_FORMAT
          )
        );
      }
    }
  }, [athleteId, currentAthlete, rangeSelector.type]);

  useEffect(() => {
    if (currentAthlete !== null && athleteId !== currentAthlete) {
      const stats = sortStatsByDate(
        filterUnsetValues(dailyStats, FIELD),
        "recent"
      );
      setStatsData(stats);
    }
  }, [athleteId, currentAthlete, dailyStats]);

  const updateState = useCallback((state) => {
    setRangeSelector((s) => state.rangeSelector ?? s);
    setDateFrom((s) => state.dateFrom ?? s);
    setDateTo((s) => state.dateTo ?? s);
    if (state.sortedTableData !== undefined)
      setSortedTableData(state.sortedTableData);
    if (state.sortedRecent !== undefined) setSortedRecent(state.sortedRecent);
  }, []);

  const sortList = useCallback((newState) => {
    setSortedTableData(newState.dateFormattedData);
    setSortedRecent(newState.sortedRecent);
  }, []);

  const listData = filterStatsbyDateRange({
    stats: statsData,
    dateTo,
    dateFrom,
  });

  const totalWater = listData.reduce(
    (prev, stat) => prev + Number(stat[FIELD] || 0),
    0
  );
  const avg = listData.length ? round(totalWater / listData.length, 1) : 0;
  const avgDisplay = avg
    ? isMetric
      ? `${avg} L`
      : `${round(avg * OZ_PER_L, 0)} oz`
    : 0;
  const headerRight = {
    name: t("waterIntakePage.average"),
    value: avgDisplay,
  };

  const chartListData = listData.map((stat) => ({
    ...stat,
    [FIELD]: isMetric
      ? Number(stat[FIELD]).toFixed(1)
      : round(Number(stat[FIELD]) * OZ_PER_L, 0),
  }));
  console.log("chartListData", chartListData);
  const chartData = readyChartData({ stats: chartListData, field: FIELD });

  const labels = getLabels(listData).reverse();
  const colors = ["rgba(59, 130, 246, 0.8)"];

  const statsList = mapUtcToDatePicker({
    stats: listData,
    fieldToConvert: "utcDate",
    newField: "date",
  }).map((stat) => ({
    ...stat,
    waterDisplay: isMetric
      ? Number(stat[FIELD]).toFixed(1)
      : round(Number(stat[FIELD]) * OZ_PER_L, 0),
  }));

  const formatValue = (val) =>
    isMetric ? `${Number(val).toFixed(1)}` : `${round(Number(val), 0)}`;

  const columnsData = [
    {
      name: t("waterIntakePage.dateColumn"),
      field: "date",
      columnClass: "date",
      itemClass: "date",
    },
    {
      name: isMetric ? t("waterIntakePage.waterColumnL") : t("waterIntakePage.waterColumnOz"),
      field: "waterDisplay",
      columnClass: "steps",
      itemClass: "waterIntake",
    },
  ];

  const chartOptions = {
    showLine: true,
    ticks: [],
    axisY: {
      offset: 50,
      labelOffset: { x: 5, y: 5 },
      labelInterpolationFnc: (value) => formatValue(value),
    },
  };

  const tooltipUnit = isMetric ? " L" : " oz";

  return (
    <main role="reports-wrapper">
      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={locals.athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(locals?.athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>
      <NavigationComponent
        query={query}
        text={t("waterIntakePage.calendarDayView")}
        pathName={pathname}
        classNames="mobile-hidden"
      />
      <article id="water-intake" role="report-content">
        <ReportsBackHandler title={t("waterIntakePage.title")} locals={locals} />
        <header className="mobile-hidden">
          <hgroup>
            <h2 className="mobile-hidden">{t("waterIntakePage.title")}</h2>
          </hgroup>
        </header>
        <RangeContainerComponent
          rangeSelector={rangeSelector}
          dateFrom={dateFrom}
          dateTo={dateTo}
          quickDisabled={false}
          statsData={statsData}
          hoveredDate={hoveredDate}
          sortedRecent={sortedRecent}
          sortedTableData={sortedTableData}
          updateState={(state) => {
            updateState({ ...state, sortedTableData: null });
          }}
          getDateFirstEntry={getDateFirstEntry}
          locals={locals}
        />
        <BarChartComponent
          bodyMeasurement={true}
          dateTo={dateTo}
          dateFrom={dateFrom}
          series={chartData}
          labels={labels}
          chartName="Water"
          displayLeyend={false}
          colors={colors}
          areaOpacity={0.3}
          updateHoveredStat={setHoveredDate}
          chartOptions={chartOptions}
          headerRight={headerRight}
          tooltipUnit={tooltipUnit}
          type="Bar"
        />
        <div className="mobile-hidden">
          <RPEList
            dateFrom={dateFrom}
            dateTo={dateTo}
            list={sortedTableData || statsList}
            columnsData={columnsData}
            highlightedElem={hoveredDate}
            sortedRecent={sortedRecent}
            updateState={sortList}
          />
        </div>
        <div className="only-show-mobile">
          <RPEList
            dateFrom={dateFrom}
            dateTo={dateTo}
            list={sortedTableData || statsList}
            columnsData={columnsData}
            highlightedElem={hoveredDate}
            sortedRecent={sortedRecent}
            updateState={sortList}
          />
        </div>
      </article>
    </main>
  );
}

export default WaterIntake;
